import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import moment from 'moment';
import { ArAgingRow } from './types';

export interface ColumnHeader {
  key: string;
  label: string;
}

const formatRowForExport = (
  row: ArAgingRow,
  columns: ColumnHeader[]
): Record<string, string | number> => {
  const formatted: Record<string, string | number> = {};
  columns.forEach((header) => {
    const value = row[header.key as keyof ArAgingRow];
    if (header.key === 'lastCollectionActivity') {
      const activity = row.lastCollectionActivity;
      formatted[header.label] = activity?.date
        ? `${moment(activity.date).format('MM/DD/YYYY')}${
            activity.type ? ` · ${activity.type}` : ''
          } (${activity.countLast30Days} in last 30 days)`
        : '';
    } else if (header.key === 'lastPaymentReceived') {
      const payment = row.lastPaymentReceived;
      formatted[header.label] = payment?.date
        ? `${moment(payment.date).format('MM/DD/YYYY')} · $${payment.amount.toFixed(2)}`
        : '';
    } else if (typeof value === 'boolean') {
      formatted[header.label] = value ? 'Yes' : 'No';
    } else if (typeof value === 'number' && !isNaN(value)) {
      // Keep the actual number, including negatives (credits) and zero.
      formatted[header.label] = value;
    } else if (typeof value === 'string') {
      formatted[header.label] = value;
    } else {
      formatted[header.label] = 0;
    }
  });
  return formatted;
};

// MM-DD-YYYY stamp for export file names, e.g. "ar-aging-report-07-23-2026".
// Dashes instead of slashes since "/" is a path separator in download filenames.
const getExportDateStamp = () => moment().format('MM-DD-YYYY');

export const exportToXlsx = (rows: ArAgingRow[], columns: ColumnHeader[]) => {
  const formattedRows = rows.map((r) => formatRowForExport(r, columns));
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'AR Aging Report');
  XLSX.writeFile(workbook, `ar-aging-report-${getExportDateStamp()}.xlsx`);
};

export const exportToCsv = (rows: ArAgingRow[], columns: ColumnHeader[]) => {
  const formattedRows = rows.map((r) => formatRowForExport(r, columns));
  const csv = Papa.unparse(formattedRows, { columns: columns.map((c) => c.label) });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `ar-aging-report-${getExportDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
